import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projects as projectsApi, ai as aiApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../lib/theme";
import { ensureProjectDefaults, computeProgress, describePatch, applyProjectSkin, debtKanbanCards, worldTilesetPng } from "../lib/gamecraft";
import { ProgressRing, AIAssistantBar } from "../components/gc/ui";
import {
  House, Folder, Lightbulb, BookOpen, Palette, MusicNotes, Code,
  Graph, Cpu, Gear, MagnifyingGlass, Bell, SignOut, Sun, Moon,
  GameController, List, CaretDown,
} from "@phosphor-icons/react";
import OverviewPanel from "../components/ide/OverviewPanel";
import ProjectPanel from "../components/ide/ProjectPanel";
import IdeasPanel from "../components/ide/IdeasPanel";
import StoryPanel from "../components/ide/StoryPanel";
import SpriteHub from "../components/ide/SpriteHub";
import AudioHub from "../components/ide/AudioHub";
import CodeHub from "../components/ide/CodeHub";
import CanvasKanbanPanel from "../components/ide/CanvasKanbanPanel";
import EngineHub from "../components/ide/EngineHub";
import AIPanel from "../components/ide/AIPanel";
import SettingsPanel from "../components/ide/SettingsPanel";
import ShareDialog from "../components/ide/ShareDialog";
import CommandPalette from "../components/ide/CommandPalette";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import ProfileAvatar from "../components/gc/ProfileAvatar";
import { firstName, displayHandle } from "../lib/profile";

const TABS = [
  { key: "overview", icon: House, i18n: "ide.tabs.overview" },
  { key: "project", icon: Folder, i18n: "ide.tabs.project" },
  { key: "ideas", icon: Lightbulb, i18n: "ide.tabs.ideas" },
  { key: "story", icon: BookOpen, i18n: "ide.tabs.story" },
  { key: "sprites", icon: Palette, i18n: "ide.tabs.sprites" },
  { key: "audio", icon: MusicNotes, i18n: "ide.tabs.audio" },
  { key: "code", icon: Code, i18n: "ide.tabs.code" },
  { key: "canvas", icon: Graph, i18n: "ide.tabs.canvas" },
  { key: "engine", icon: Cpu, i18n: "ide.tabs.engine" },
  { key: "settings", icon: Gear, i18n: "ide.tabs.settings" },
];

function Nav({ tab, setTab, collapsed }) {
  const { t } = useI18n();
  return (
    <nav className={"flex-1 overflow-y-auto gf-scroll py-2 " + (collapsed ? "px-1" : "px-2")}>
      {TABS.map((x) => {
        const Icon = x.icon;
        const active = tab === x.key;
        return (
          <button
            key={x.key}
            data-testid={`ide-tab-${x.key}`}
            onClick={() => setTab(x.key)}
            title={t(x.i18n)}
            className={"gc-nav-item mb-0.5 " + (active ? "active" : "") + (collapsed ? " justify-center px-0" : "")}
          >
            <Icon size={18} weight={active ? "fill" : "regular"} className="shrink-0" />
            {!collapsed && <span className="truncate">{t(x.i18n)}</span>}
          </button>
        );
      })}
    </nav>
  );
}

export default function ProjectIDE() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTabState] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOut, setAiOut] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const saveTimer = useRef(null);

  const setTab = useCallback((k) => { setTabState(k); setMobileNavOpen(false); }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = await projectsApi.get(projectId);
        const hydrated = ensureProjectDefaults(p);
        setProject(hydrated);
        if (!p.config?.starter_kit && hydrated.config?.starter_kit) {
          persist({
            sprites: hydrated.sprites,
            entities: hydrated.entities,
            story_items: hydrated.story_items,
            kanban: hydrated.kanban,
            soundboard: hydrated.soundboard,
            gdd: hydrated.gdd,
            diagram: hydrated.diagram,
            animations: hydrated.animations,
            tags: hydrated.tags,
            config: hydrated.config,
          });
        }
        const list = await projectsApi.list().catch(() => []);
        setProjects(list);
      } catch {
        toast.error(t("toast.notFound"));
        navigate("/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, navigate]); // eslint-disable-line

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      const updated = await projectsApi.update(projectId, patch);
      setProject(ensureProjectDefaults(updated));
    } catch {
      toast.error(t("toast.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [projectId]); // eslint-disable-line

  const updateProject = useCallback((patch) => {
    setProject((p) => {
      const next = ensureProjectDefaults({ ...p, ...patch });
      const act = {
        ts: new Date().toISOString(),
        message: describePatch(patch),
        color: "blue",
      };
      next.activity = [...(next.activity || []).slice(-40), act];
      return next;
    });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist({ ...patch }), 700);
  }, [persist]);

  useEffect(() => {
    applyProjectSkin(project?.config);
    return () => applyProjectSkin(null);
  }, [
    project?.config?.skin,
    project?.config?.accent,
    project?.config?.bg,
    project?.config?.banner,
    project?.config?.bg_image,
    project?.config?.bg_preset,
    project?.config?.bg_source,
    project?.config?.pattern,
    project?.config?.glow,
    project?.config?.accent2,
    project?.config?.tool_bg,
    project?.config?.theme_apply,
    JSON.stringify(project?.config?.theme_apply || {}),
    theme,
  ]);

  const handleAi = async (prompt, action) => {
    if (action === "engine") {
      setTab("engine");
      toast.success("Run en el motor. Cámara: " + (project.config?.camera === "topdown" ? "arriba" : "lado"));
      return;
    }
    if (action === "playlog") {
      const note = window.prompt("Nota de playtest (qué se rompió / qué se sintió / qué cortar)");
      if (!note) return;
      updateProject({ config: { ...(project.config || {}), playtest_log: [...(project.config?.playtest_log || []), { ts: new Date().toISOString(), note }] } });
      setTab("overview");
      toast.success("Nota guardada");
      return;
    }
    if (action === "debt") {
      const cards = debtKanbanCards(project);
      const columns = (project.kanban?.columns || []).map((c) => c.id === "todo" ? { ...c, cards: [...(c.cards || []), ...cards] } : c);
      updateProject({ kanban: { ...(project.kanban || {}), columns } });
      setTab("canvas");
      toast.success(`${cards.length} tarjetas de sprites faltantes`);
      return;
    }
    if (action === "tileset") {
      const skin = project.config?.skin || "forest";
      const name = `mundo_${skin}`;
      if ((project.sprites || []).some((s) => s.name === name)) {
        setTab("sprites");
        toast.message("Ya existe el tileset del mundo");
        return;
      }
      try {
        await projectsApi.addSprite(project.project_id, {
          name, data_url: worldTilesetPng(skin), category: "tileset", studio: "world", width: 16, height: 16,
        });
        const p = await projectsApi.get(project.project_id);
        updateProject({ sprites: p.sprites });
        setTab("sprites");
        toast.success("Tileset del mundo en biblioteca");
      } catch {
        toast.error("No se pudo guardar el tileset");
      }
      return;
    }
    setAiLoading(true);
    try {
      const wantsSprite = action === "sprite" || tab === "sprites" || /sprite|pixel art|genera(r|me)? (un |una )?(sprite|imagen)/i.test(prompt || "");
      if (wantsSprite) {
        const desc = (!prompt || /generar sprite/i.test(prompt)) && (prompt || "").trim().length < 24
          ? `${project.name} hero, pixel art character`
          : prompt;
        const cam = project.config?.camera === "topdown" ? "top-down Gungeon" : "side Metal Slug";
        const size = 32;
        const res = await aiApi.sprite({
          prompt: `pixel art 2D game sprite, ${cam}, transparent background, no text. ${desc}`,
          style: "pixel art",
          size,
        });
        const raw = res.data || "";
        const url = raw.startsWith("data:") ? raw : `data:${res.mime_type || "image/png"};base64,${raw}`;
        if (res.needs_api_key) {
          toast.error("Añade tu Google API key en Ajustes para generar sprites.");
          setTab("settings");
          return;
        }
        await projectsApi.addSprite(project.project_id, {
          name: (prompt || "ai-sprite").slice(0, 40),
          data_url: url,
          category: "character",
          studio: "creatures",
        });
        const p = await projectsApi.get(project.project_id);
        updateProject({ sprites: p.sprites });
        setTab("sprites");
        toast.success(
          res.source === "gemini" ? "Sprite generado e importado"
            : res.source === "pollinations" ? "Respaldo de imagen importado (no Gemini)"
            : "Plantilla procedural (ningún motor de imagen respondió)"
        );
        return;
      }
      const res = await aiApi.ideas({
        prompt: `[${tab}] ${prompt}\nJuego: ${project.name}. Idea: ${project.gdd?.idea || ""}`,
        category: tab,
        language: lang,
      });
      setAiOut(res.text || "");
      setAiOpen(true);
      toast.success("Respuesta de IA lista");
    } catch {
      toast.error(t("toast.aiFailed"));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="gc-app flex items-center justify-center min-h-screen text-sm" style={{ color: "var(--gc-muted)" }}>
        <span className="gf-blink">Cargando GameCraft 2D…</span>
      </div>
    );
  }

  const progress = computeProgress(project);

  const renderTab = () => {
    switch (tab) {
      case "overview": return <OverviewPanel project={project} onJump={setTab} onChange={updateProject} onShareClick={() => setShareOpen(true)} />;
      case "project": return <ProjectPanel project={project} onChange={updateProject} />;
      case "ideas": return <IdeasPanel project={project} onChange={updateProject} onJump={setTab} />;
      case "story": return <StoryPanel project={project} onChange={updateProject} />;
      case "sprites": return <SpriteHub project={project} onChange={updateProject} onJump={setTab} />;
      case "audio": return <AudioHub project={project} onChange={updateProject} />;
      case "code": return <CodeHub project={project} onChange={updateProject} />;
      case "canvas": return <CanvasKanbanPanel project={project} onChange={updateProject} />;
      case "engine": return <EngineHub project={project} onChange={updateProject} />;
      case "settings": return <SettingsPanel project={project} onChange={updateProject} />;
      default: return null;
    }
  };

  const sidebar = (collapsed) => (
    <>
      <div className={"flex items-center gap-2 py-4 " + (collapsed ? "px-2 justify-center" : "px-4")}>
        <div className="w-9 h-9 rounded-xl bg-sky-500 text-white flex items-center justify-center shrink-0">
          <GameController size={18} weight="fill" />
        </div>
        {!collapsed && <div className="font-display font-bold leading-tight">GameCraft<br /><span className="text-sky-500 text-xs">2D</span></div>}
      </div>
      <Nav tab={tab} setTab={setTab} collapsed={collapsed} />
      {!collapsed && (
        <div className="px-4 py-3 flex flex-col items-center">
          <ProgressRing value={progress} size={84} label="Progreso" />
        </div>
      )}
      {!collapsed && (
        <button
          type="button"
          onClick={() => setTab("settings")}
          className="px-4 py-3 flex items-center gap-2 border-t text-left w-full"
          style={{ borderColor: "var(--gc-border)" }}
        >
          <ProfileAvatar user={user} size={36} />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || "Creador"}</div>
            <div className="text-[11px] truncate" style={{ color: "var(--gc-muted)" }}>
              {user?.studio || displayHandle(user) || "Editar perfil"}
            </div>
          </div>
        </button>
      )}
      <div className={"py-3 flex items-center gap-2 border-t " + (collapsed ? "justify-center px-1" : "px-4")} style={{ borderColor: "var(--gc-border)" }}>
        <button onClick={toggle} className="gc-btn gc-btn-ghost h-9 w-9 p-0" title="Tema">
          {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        {!collapsed && (
          <button onClick={async () => { await logout(); navigate("/"); }} className="gc-btn gc-btn-ghost h-9 w-9 p-0 ml-auto text-red-400">
            <SignOut size={16} />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="h-screen flex flex-col gc-app overflow-hidden">
      <header className="gc-glass mx-3 mt-3 px-3 md:px-4 py-2.5 flex items-center gap-3 z-30" style={{ borderRadius: 20 }}>
        <button className="md:hidden p-2" onClick={() => setMobileNavOpen(true)}><List size={18} /></button>
        <button onClick={() => navigate("/dashboard")} className="hidden md:flex gc-btn gc-btn-ghost h-9 px-3 text-xs">Proyectos</button>
        <div className="relative">
          <select
            className="gc-input h-9 pl-3 pr-8 text-sm font-semibold appearance-none min-w-[180px]"
            value={project.project_id}
            onChange={(e) => navigate(`/project/${e.target.value}`)}
          >
            {projects.length ? projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>) : <option value={project.project_id}>{project.name}</option>}
          </select>
          <CaretDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <button onClick={() => setCmdkOpen(true)} className="flex-1 max-w-xl mx-auto hidden sm:flex items-center gap-2 gc-input h-10 px-4 text-sm" style={{ color: "var(--gc-muted)" }}>
          <MagnifyingGlass size={14} /> Buscar en el proyecto
          <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-black/5">Ctrl + K</kbd>
        </button>
        <div className="flex items-center gap-1 ml-auto">
          <span className="hidden md:inline text-[11px]" style={{ color: "var(--gc-muted)" }}>{saving ? "Guardando…" : "Guardado"}</span>
          <button onClick={toggle} className="gc-btn gc-btn-ghost h-9 w-9 p-0">{theme === "light" ? <Sun size={16} /> : <Moon size={16} />}</button>
          <div className="relative">
            <button className="gc-btn gc-btn-ghost h-9 w-9 p-0" onClick={() => setBellOpen((v) => !v)} title="Actividad">
              <Bell size={16} />
              {(project.activity || []).length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-sky-500" />}
            </button>
            {bellOpen && (
              <div className="absolute right-0 top-11 w-72 gc-card p-3 z-50 max-h-80 overflow-auto">
                <div className="text-xs font-semibold mb-2">Actividad</div>
                {(project.activity || []).slice(-8).reverse().map((a, i) => (
                  <div key={i} className="text-xs py-1" style={{ color: "var(--gc-muted)" }}>{a.message}</div>
                ))}
                {!(project.activity || []).length && <p className="text-xs" style={{ color: "var(--gc-muted)" }}>Sin actividad aún.</p>}
                {(project.config?.playtest_log || []).slice(-3).reverse().map((x, i) => (
                  <div key={`p${i}`} className="text-xs py-1 border-t mt-1 pt-1" style={{ borderColor: "var(--gc-border)", color: "var(--gc-muted)" }}>
                    Playtest: {x.note}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setLang(lang === "es" ? "en" : "es")} className="gc-btn gc-btn-ghost h-9 px-2 text-xs">{lang.toUpperCase()}</button>
          <button
            type="button"
            onClick={() => setTab("settings")}
            className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-full hover:bg-black/5"
            title="Tu perfil"
          >
            <ProfileAvatar user={user} size={32} />
            <span className="hidden lg:block text-left leading-tight">
              <span className="block text-xs font-semibold">{firstName(user)}</span>
              {user?.studio && <span className="block text-[10px]" style={{ color: "var(--gc-muted)" }}>{user.studio}</span>}
            </span>
          </button>
        </div>
      </header>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={project} onProjectChange={(patch) => setProject((p) => ({ ...p, ...patch }))} />
      <CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} tabs={TABS} project={project} onJump={(k) => { if (k === "ai") setAiOpen(true); else setTab(k); }} onShare={() => setShareOpen(true)} />

      <div className="flex-1 flex overflow-hidden px-3 pb-3 pt-3 gap-3 min-h-0">
        <aside className="hidden md:flex gc-glass w-56 flex-col overflow-hidden" style={{ borderRadius: 24 }}>
          {sidebar(false)}
        </aside>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="p-0 w-64" style={{ background: "var(--gc-sidebar)" }}>
            <SheetTitle className="sr-only">Navegación</SheetTitle>
            <div className="flex flex-col h-full">{sidebar(false)}</div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col min-w-0 gap-3">
          <main className="flex-1 overflow-auto gf-scroll gc-glass" style={{ borderRadius: 24 }}>
            {renderTab()}
          </main>
          <AIAssistantBar onSend={handleAi} loading={aiLoading} />
        </div>
      </div>

      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-auto" style={{ background: "var(--gc-sidebar)" }}>
          <SheetTitle>Asistente IA</SheetTitle>
          {aiOut && <div className="gc-card p-4 mt-3 whitespace-pre-wrap text-sm">{aiOut}</div>}
          <div className="mt-4"><AIPanel project={project} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
